/**
 * /slides 카드 우상단 "…" 메뉴.
 * ChatSessionMenu 패턴 (createPortal + position:fixed + 자동 닫힘) 복제.
 *
 * 항목: 즐겨찾기 토글 / 이름 변경(prompt) / 복제 / 삭제(confirm).
 * 카드 자체가 <Link> 라 메뉴 버튼 클릭은 e.stopPropagation + e.preventDefault 로 link 발화 막음.
 */
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { SlideSummary } from '@innermate/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Copy, MoreHorizontal, Pencil, Star, StarOff, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface Props {
  slide: SlideSummary;
}

export function SlideCardMenu({ slide }: Props) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const favoriteMut = useMutation({
    mutationFn: () => api.slideFavorite(slide.id, { isFavorite: !slide.isFavorite }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['slides', 'list'] }),
  });
  const renameMut = useMutation({
    mutationFn: (title: string) => api.slideRename(slide.id, { title }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['slides', 'list'] }),
  });
  const duplicateMut = useMutation({
    mutationFn: () => api.slideDuplicate(slide.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['slides', 'list'] }),
  });
  const deleteMut = useMutation({
    mutationFn: () => api.slideDelete(slide.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['slides', 'list'] }),
  });

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (btnRef.current?.contains(e.target as Node)) return;
      if (menuRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onScrollOrResize = () => setOpen(false);
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  const toggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      // 메뉴 폭 ~176px. 우상단에 카드 트리거 → 메뉴는 좌하단에 펼치기 (오른쪽 잘림 회피).
      const menuW = 176;
      setPos({
        top: rect.bottom + 4,
        left: Math.max(8, rect.right - menuW),
      });
    }
    setOpen((v) => !v);
  };

  const close = () => setOpen(false);

  const onRename = () => {
    close();
    const next = window.prompt('새 슬라이드 제목', slide.title);
    if (next == null) return;
    const trimmed = next.trim();
    if (trimmed.length === 0 || trimmed === slide.title) return;
    renameMut.mutate(trimmed);
  };

  const onDelete = () => {
    close();
    if (window.confirm('이 슬라이드를 삭제할까요?')) {
      deleteMut.mutate();
    }
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label="슬라이드 메뉴"
        onClick={toggle}
        className="size-7 inline-grid place-items-center rounded-md text-foreground-muted bg-background/80 hover:bg-surface-muted/80 hover:text-foreground backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <MoreHorizontal className="size-4" />
      </button>
      {open && pos
        ? createPortal(
            <div
              ref={menuRef}
              style={{ position: 'fixed', top: pos.top, left: pos.left }}
              className="w-44 z-50 rounded-md border border-border bg-surface shadow-lg py-1 text-sm"
            >
              <MenuItem
                icon={slide.isFavorite ? StarOff : Star}
                onClick={() => {
                  close();
                  favoriteMut.mutate();
                }}
              >
                {slide.isFavorite ? '즐겨찾기 해제' : '즐겨찾기'}
              </MenuItem>
              <MenuItem icon={Pencil} onClick={onRename}>
                이름 변경
              </MenuItem>
              <MenuItem
                icon={Copy}
                onClick={() => {
                  close();
                  duplicateMut.mutate();
                }}
              >
                복제
              </MenuItem>
              <div className="my-1 border-t border-border" />
              <MenuItem icon={Trash2} danger onClick={onDelete}>
                삭제
              </MenuItem>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function MenuItem({
  children,
  onClick,
  icon: Icon,
  danger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  icon: React.ComponentType<{ className?: string }>;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        'flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm hover:bg-surface-muted',
        danger ? 'text-[hsl(0_72%_55%)]' : 'text-foreground',
      )}
    >
      <Icon className="size-4 shrink-0" />
      <span>{children}</span>
    </button>
  );
}
